/**
 * Persistent top-of-app trial-countdown banner (M22).
 *
 * Renders a thin (~36px) banner with "X days left in trial" + an "Upgrade Now"
 * CTA. Yellow when more than 3 days remain, red in the last 3 days.
 *
 * Hides itself in three cases:
 *   - the trial-status query is still loading or errored
 *   - the tenant is on a paid plan (`isTrial === false`)
 *   - the user dismissed it for the current page (Zustand-only state — resets
 *     on navigation/refresh, never persisted)
 *
 * The actual mount-point lives in <RootLayout/>; this component is layout-
 * agnostic and can be embedded anywhere above the page <main>.
 */
import { X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { create } from 'zustand';

import { UpgradeDialog } from './UpgradeDialog';

import { Button } from '@/components/ui/button';
import { useTrialStatus } from '@/features/subscription/subscription.api';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Session-scoped dismissal store (NOT persisted — banner reappears on reload)
// ---------------------------------------------------------------------------

interface BannerDismissState {
  dismissed: boolean;
  dismiss: () => void;
  reset: () => void;
}

const useBannerDismiss = create<BannerDismissState>((set) => ({
  dismissed: false,
  dismiss: () => set({ dismissed: true }),
  reset: () => set({ dismissed: false }),
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TrialCountdownBanner(): ReactNode {
  const { data: trial, isLoading, isError } = useTrialStatus();
  const dismissed = useBannerDismiss((s) => s.dismissed);
  const dismiss = useBannerDismiss((s) => s.dismiss);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  if (isLoading || isError || !trial) return null;
  if (!trial.isTrial) return null;
  if (dismissed) return null;

  const daysLeft = Math.max(0, trial.daysRemaining);
  const isCritical = daysLeft <= 3;
  const expired = trial.isExpired || daysLeft === 0;

  const wrapperColor = isCritical
    ? 'bg-red-600 text-white border-red-700'
    : 'bg-amber-400 text-amber-950 border-amber-500';

  const message = expired
    ? 'Your trial has expired — upgrade now to keep using HRMS'
    : `${String(daysLeft)} day${daysLeft === 1 ? '' : 's'} left in your free trial`;

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'relative flex h-9 w-full items-center justify-center gap-3 border-b px-4 text-xs font-medium sm:text-sm',
          wrapperColor,
        )}
      >
        <span className="truncate">{message}</span>

        <Button
          size="sm"
          variant="secondary"
          className={cn(
            'h-6 px-2.5 py-0 text-xs font-semibold',
            isCritical ? 'bg-white text-red-700 hover:bg-red-50' : 'bg-white text-amber-900',
          )}
          onClick={() => setUpgradeOpen(true)}
        >
          Upgrade Now
        </Button>

        <button
          type="button"
          aria-label="Dismiss trial banner"
          onClick={dismiss}
          className={cn(
            'absolute right-2 inline-flex h-5 w-5 items-center justify-center rounded transition-colors',
            isCritical ? 'hover:bg-red-700' : 'hover:bg-amber-500',
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </>
  );
}
